import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient';
import type { OperatingHours } from '@/types/operatingHours';

interface RegisterGymProfileRequest {
  name: string;
  address: string;
  lat: number;
  lng: number;
  phone: string;
  description: string;
  features: string[];
  amenities: string[];
  facility_types?: string[];
  operating_hours?: OperatingHours | null;
  price_min: number;
  price_max: number;
  initiation_fee: number;
  owner_id: string;
  images: string[];
  status: string;
  equipment?: { category_id: string; item_name: string; quantity: number }[];
  pricing_type?: 'per_month' | 'per_class';
  price_per_month?: number | null;
  price_per_year?: number | null;
  price_per_day_pass?: number | null;
  price_per_class?: number | null;
  website_url?: string | null;
  day_pass_is_free_trial?: boolean;
  hours_by_appointment_only?: boolean;
  song_title?: string | null;
  song_artist?: string | null;
  what_to_know?: string | null;
}

type GymProfile = Omit<RegisterGymProfileRequest, 'owner_id'> & {
  id: string;
  owner_id: string;
};

async function getOwnerGymProfile(ownerId: string): Promise<GymProfile | null> {
  const { data, error } = await supabaseClient
    .from('gyms')
    .select('*')
    .eq('owner_id', ownerId)
    .eq('onboarding_draft', false)
    .single();
  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    throw new Error(error.message);
  }

  return {
    id: data.id,
    owner_id: data.owner_id,
    name: data.name,
    address: data.address,
    lat: data.lat,
    lng: data.lng,
    phone: data.phone,
    description: data.description,
    features: (data.features || []).map(String),
    amenities: (data.amenities || []).map(String),
    facility_types: (data.facility_types || []).map(String),
    price_min: data.price_min,
    price_max: data.price_max,
    initiation_fee: data.initiation_fee,
    operating_hours: data.operating_hours ?? null,
    images: data.images || [],
    status: data.status,
    equipment: data.equipment ?? [],
    pricing_type: data.pricing_type ?? 'per_month',
    price_per_month: data.price_per_month ?? null,
    price_per_year: data.price_per_year ?? null,
    price_per_day_pass: data.price_per_day_pass ?? null,
    price_per_class: data.price_per_class ?? null,
    website_url: data.website_url ?? '',
    day_pass_is_free_trial: data.day_pass_is_free_trial ?? false,
    hours_by_appointment_only: data.hours_by_appointment_only ?? false,
    song_title: data.song_title ?? null,
    song_artist: data.song_artist ?? null,
    what_to_know: data.what_to_know ?? null,
  };
}

export function useOwnerGymProfile(ownerId: string) {
  return useQuery({
    queryKey: ['gym-profile', ownerId],
    queryFn: () => getOwnerGymProfile(ownerId),
    enabled: !!ownerId,
    staleTime: 5 * 60 * 1000,
  });
}
